import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { getBrand, Brand } from 'api/getBrand'
import { customBaseDomainFromHost } from 'scripts/customBaseDomain'

// OEM / ホワイトラベル対応: partner のブランド情報を React Context で子に配布する。
// 初回マウント時に locked-api の /api/v1/public/brand を叩き、取得結果を state に格納。

const DEFAULT_BRAND: Brand = {
  host: null,
  productDisplayName: 'LOCKED',
  companyName: null,
  logoUrl: '/img/icn_lockedlogo.png',
  // OEM: LOCKED は link を追加せず static/favicon.ico を使うため null。
  // logoUrl の既定画像を favicon へ流用しない。
  faviconUrl: null,
  customBaseDomain: null,
  termsUrl: null,
  privacyPolicyUrl: null,
  supportEmail: null,
  supportUrl: null,
  supportPhone: null,
  zendeskFormUrl: null,
  zendeskFormId: null,
  // OEM: 拡張 DL ボタンの URL。既定は null（= ボタン非表示）でリーク安全側に倒す。partner ホストで
  // 旧 api/auth が本フィールドを返さない場合でも LOCKED ストアへ誘導しない。通常の LOCKED は
  // api/auth が LOCKED の URL を返すためボタンは出る。
  chromeWebStoreUrl: null,
  // OEM: LOCKED 既定はブランドカラーを持たない（null → FE 既定色のまま）。
  brandColor: null,
  // OEM: LOCKED 既定はロゴ幅を持たない（null → resolveLogoWidth が既定 96px を返す）。
  logoWidth: null,
  fallback: true,
}

// OEM / ホワイトラベル: partner のカスタムドメインで brand 取得がリトライ枯渇して失敗確定したときの
// フォールバック先。DEFAULT_BRAND（フル LOCKED ブランド）に倒すと、partner エンドユーザーに LOCKED
// ロゴ・「LOCKED 認証画面」title・LOCKED ヘルプ・「Onetap, Inc」コピーライトがフル表示されてしまう。
// 「partner ドメインと判明しているがブランド未解決」の中立状態を表すため、LOCKED 専用導線が一切
// 出ないフィールド値にする:
//   - productDisplayName / logoUrl / companyName は空文字列。表示側は空文字列を「中立（LOCKED
//     デフォルトに倒さない）」の sentinel として扱う（BrandedLogo はロゴ非表示 placeholder、
//     copyrightCompanyName は社名非表示、BrandedHelmetTitle は中立 title）。
//   - fallback:false。fallback===true をトリガーにする LOCKED 専用 UI（LockedHelpLink / root の
//     FAQ ボタン / ExtensionGuideModal の Notion ヘルプ・LOCKED ガイド画像）を出さない。
//     favicon も blank（!fallback）になり LOCKED favicon を出さない。
//   - support 系 URL は null（SupportLink は URL 無しで非表示）。chromeWebStoreUrl も null。
const NEUTRAL_BRAND: Brand = {
  host: null,
  productDisplayName: '',
  companyName: '',
  logoUrl: '',
  // OEM: 中立時も partner favicon は未設定のため null。logoUrl の空文字 sentinel とは異なり、
  // favicon は DEFAULT_BRAND と同じ null に揃え、表示側で従来の白画像へフォールバックする。
  faviconUrl: null,
  customBaseDomain: null,
  termsUrl: null,
  privacyPolicyUrl: null,
  supportEmail: null,
  supportUrl: null,
  supportPhone: null,
  zendeskFormUrl: null,
  zendeskFormId: null,
  chromeWebStoreUrl: null,
  // OEM: 中立フォールバックも色は持たない（既定色のまま。LOCKED 既定色を露出させない）。
  brandColor: null,
  // OEM: 中立フォールバックも幅を持たない。ロゴ自体が非表示（logoUrl:''）なので既定幅で無害。
  logoWidth: null,
  fallback: false,
}

// brand 取得がリトライ枯渇して失敗確定したときのフォールバック先を、ホストから決める。
// partner のカスタムドメイン（customBaseDomainFromHost が non-null）→ NEUTRAL_BRAND（中立）。
// LOCKED ドメイン（null）→ 従来どおり DEFAULT_BRAND（フル LOCKED。後方互換）。
// brand 取得なしでホスト名だけで判定できるため、取得失敗時も partner / LOCKED を取り違えない。
const failureFallbackBrand = (): Brand => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  return customBaseDomainFromHost(hostname) ? NEUTRAL_BRAND : DEFAULT_BRAND
}

// 取得 / cache 由来の brand（sparse の可能性あり）を欠落フィールド込みで完成させる。
// ベースの選び方が肝:
//   - fallback:false（= 解決済み partner ブランド）: NEUTRAL_BRAND をベースにする。partner なのに API が
//     productDisplayName / logoUrl 等を省いて返したとき、欠落フィールドを DEFAULT_BRAND（LOCKED）の値で
//     埋めると partner ホストで LOCKED の名称・ロゴが露出する。中立値（空文字 / 非表示）で補完して防ぐ。
//   - fallback:true / 省略（= LOCKED 既定 or 疎な fallback）: 従来どおり DEFAULT_BRAND（フル LOCKED）で補完。
//     LOCKED ドメインの後方互換を維持する。
// fallback 自体は `{...base, ...res}` の順で res が後勝ち。res が fallback を省略した場合は base 由来に
// なるため明示的に `res.fallback ?? base.fallback` で確定させ、L「if (merged.fallback)」の cache 判定が
// ベース差で揺れないようにする（解決済み partner=false→writeCachedBrand、LOCKED 既定=true→clearCachedBrand）。
const mergeBrand = (res: Partial<Brand>): Brand => {
  const base = res.fallback === false ? NEUTRAL_BRAND : DEFAULT_BRAND
  return { ...base, ...res, fallback: res.fallback ?? base.fallback }
}

const BrandContextInner = createContext<Brand>(DEFAULT_BRAND)

// FOUC 低減: 同一ホストの前回ブランドを sessionStorage に短期キャッシュし、
// 取得完了前に即適用する（初回訪問のみ DEFAULT、以降はチラつかない）。
const CACHE_KEY = (): string => `oem-brand:${window.location.host}`
const readCachedBrand = (): Brand | null => {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY())
    return raw ? (JSON.parse(raw) as Brand) : null
  } catch {
    return null
  }
}
const writeCachedBrand = (brand: Brand): void => {
  try {
    window.sessionStorage.setItem(CACHE_KEY(), JSON.stringify(brand))
  } catch {
    // ignore（プライベートモード等で sessionStorage が使えないケース）
  }
}
const clearCachedBrand = (): void => {
  try {
    window.sessionStorage.removeItem(CACHE_KEY())
  } catch {
    // ignore
  }
}

// brand 取得の解決状態を配布する（FOUC 対策: 解決前は brand 依存 UI（logo / title / support）を
// 出さない判断に使える）。
const BrandLoadingContext = createContext<boolean>(true)

// OEM: partner ブランドカラーを CSS 変数 --brand-primary に流す。tailwind の `locked` 色
// （var(--brand-primary, 既定)）と直書き hex の var 化箇所がこの変数を参照して partner 色に追従する。
// 不正値（hex でない）は無視して既定色のまま。未設定 / LOCKED（null）は変数を外して既定色に戻す。
const BRAND_COLOR_HEX = /^#[0-9a-fA-F]{6}$/

export const BrandProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [brand, setBrand] = useState<Brand>(DEFAULT_BRAND)
  const [loading, setLoading] = useState<boolean>(true)

  // OEM / ホワイトラベル: brand.brandColor を documentElement の --brand-primary に反映する。
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (brand.brandColor && BRAND_COLOR_HEX.test(brand.brandColor)) {
      root.style.setProperty('--brand-primary', brand.brandColor)
    } else {
      root.style.removeProperty('--brand-primary')
    }
  }, [brand.brandColor])

  useEffect(() => {
    if (typeof window === 'undefined') return
    // SSR との hydration 不整合を避けるため、キャッシュ適用は effect 内で行う
    const cached = readCachedBrand()
    if (cached) {
      // cache hit は即時表示（fetch は背景で更新）。cache miss の初回だけ解決まで待つ。
      // 成功 fetch と同じ規則でマージする（mergeBrand 参照）。cache には writeCachedBrand 経由で
      // !fallback（= 解決済みブランド）だけが書かれるため、欠落フィールドは NEUTRAL_BRAND（中立）で
      // 補完され、疎な cache でも LOCKED の名称/ロゴが露出しない。古い/疎な cache（例: chromeWebStoreUrl
      // 等の新フィールドを欠く）でも欠落フィールドが undefined にならず中立値で埋まる。
      setBrand(mergeBrand(cached))
      setLoading(false)
    }
    ;(async () => {
      try {
        // same-origin endpoint が request.host から解決するため host は渡さない。
        // getBrand は base 無し / 非 2xx / network error / timeout / CORS をすべて null に正規化して
        // 返す（throw しない。正常な未解決は server が 200 + fallback:true を返すため null にはならない）。
        // null は実際の取得失敗のみなので、partner ホストが一時的な失敗（オフライン/timeout 含む）で
        // LOCKED 固定にならないよう数回リトライしてから（cache が無ければ）フォールバックする。
        // リトライ中は loading のままで brand 依存 UI（logo/help/copyright/favicon）が LOCKED を出さない。
        const MAX_RETRY = 2
        let res = await getBrand()
        for (let attempt = 0; res === null && attempt < MAX_RETRY; attempt++) {
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1)),
          )
          res = await getBrand()
        }
        if (res) {
          // sparse body（一部フィールド欠落）でも欠落フィールドを適切なベースで補完する（mergeBrand 参照）。
          const merged = mergeBrand(res)
          setBrand(merged)
          // 定的 LOCKED(fallback) はキャッシュしない。実ブランド復活時にすぐ反映され、
          // partner ブランド解除後に古い OEM brand を stale 表示し続けない（server 側 BrandClient と挙動を揃える）。
          if (merged.fallback) {
            clearCachedBrand()
          } else {
            writeCachedBrand(merged)
          }
        } else if (!readCachedBrand()) {
          // リトライ枯渇で取得失敗が確定（res===null）かつ cache 無し。
          // partner ドメインなら NEUTRAL_BRAND（中立: LOCKED 要素を一切出さない）、
          // LOCKED ドメインなら DEFAULT_BRAND（フル LOCKED）に倒す。host から brand 取得なしで判定する。
          // cache があれば（前段で適用済みの）partner ブランドを維持し、LOCKED にフラッシュさせない。
          setBrand(failureFallbackBrand())
        }
      } catch {
        // 一時的失敗: cache があればそれを維持する（transient な失敗で LOCKED にフラッシュさせない）。
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  return (
    <BrandLoadingContext.Provider value={loading}>
      <BrandContextInner.Provider value={brand}>
        {children}
      </BrandContextInner.Provider>
    </BrandLoadingContext.Provider>
  )
}

export const useBrand = (): Brand => useContext(BrandContextInner)

// brand fetch が settle するまで true。logo / title / support 等は解決まで描画を待つと FOUC を防げる。
export const useBrandLoading = (): boolean => useContext(BrandLoadingContext)
